import React, { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { FeedSelect } from 'features/feed';
import { CustomSVG, Layouts, PaddedPaper, Video } from 'components';
import { getAppImage } from 'helpers';
import * as paths from 'paths';

import { PROFILE_VIDEO } from '../constants';
import {
  SetupInformation,
  SetupProfileButton,
  SetupSocials,
  SetupWorkspaceButton,
} from '../components';

const useStyles = makeStyles(theme => ({
  paper: {
    border: 0,
  },
  number: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '50%',
    width: 36,
    height: 36,
  },
  divider: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(4),
  },
}));

const SetupProfile: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  function handleSharePost() {
    history.push(paths.index);
  }

  function renderSidebar() {
    return (
      <FeedSelect>
        <SetupWorkspaceButton />
        <SetupProfileButton />
      </FeedSelect>
    );
  }

  function renderMain() {
    return (
      <PaddedPaper padding="48px 24px" className={classes.paper}>
        <Grid container>
          <Grid item xs>
            <Typography variant="h2">{t('setup:setupProfileHeader')}</Typography>
            <Typography variant="h5">{t('setup:setupProfileSubHead')}</Typography>
          </Grid>
          <Grid item>
            <img width={350} {...getAppImage('illustrations/thinking.png')} />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.number}>
              <Typography color="primary" variant="h6">
                1
              </Typography>
            </div>
          </Grid>
          <Grid item>
            <Typography variant="h4">{t('setup:setupProfileStep1Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep1SubHead')}</Typography>
            <Box marginTop={3}>
              <Video disableHeight video={PROFILE_VIDEO} />
            </Box>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.number}>
              <Typography color="primary" variant="h6">
                2
              </Typography>
            </div>
          </Grid>
          <Grid item>
            <Typography variant="h4">{t('setup:setupProfileStep2Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep2SubHead')}</Typography>
            <SetupInformation />
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.number}>
              <Typography color="primary" variant="h6">
                3
              </Typography>
            </div>
          </Grid>
          <Grid item>
            <Typography variant="h4">{t('setup:setupProfileStep3Header')}</Typography>
            <Typography color="textSecondary">{t('setup:setupProfileStep3SubHead')}</Typography>
            <SetupSocials />
          </Grid>
        </Grid>
        <Divider className={classes.divider} />
        <Grid container spacing={2}>
          <Grid item>
            <div className={classes.number}>
              <Typography color="primary" variant="h6">
                4
              </Typography>
            </div>
          </Grid>
          <Grid item>
            <Typography variant="h4">{t('setup:setupProfileStep4Header')}</Typography>
            <Typography paragraph color="textSecondary">
              {t('setup:setupProfileStep4SubHead')}
            </Typography>
            <Box marginTop={3}>
              <Button
                color="primary"
                onClick={handleSharePost}
                startIcon={<CustomSVG name="share" />}
                variant="contained"
              >
                {t('setup:setupProfileShareButton')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </PaddedPaper>
    );
  }

  return <Layouts.App2Column gutterBottom sidebar={renderSidebar()} main={renderMain()} />;
};

export default SetupProfile;
